"use client";

import { SceneKnowledgeBaseDetail, api } from "@/api";
import { Popover } from "antd";
import { useEffect, useMemo, useState } from "react";
import Button from "../Button";
import AbstractMVIcon from "./assets/knowledge-icon/abstract-mv.svg";
import MoreIcon from "./assets/knowledge-icon/more-icon.svg";
import PerformanceMVIcon from "./assets/knowledge-icon/performance-mv.svg";
import StoryMVIcon from "./assets/knowledge-icon/story-mv.svg";
import ViralShotsIcon from "./assets/knowledge-icon/viral-shots.svg";

interface KnowledgeBaseSelectorProps {
  onSelect: (item: { id: string; text: string }) => void;
}

const PUBLIC_KNOWLEDGE_BASES = [
  {
    id: "StoryMV",
    text: "Story MV",
    icon: <StoryMVIcon className="w-5 h-5" />,
  },
  {
    id: "AbstractMV",
    text: "Abstract MV",
    icon: <AbstractMVIcon className="w-5 h-5" />,
  },
  {
    id: "PerformanceMV",
    text: "Performance MV",
    icon: <PerformanceMVIcon className="w-5 h-5" />,
  },
  {
    id: "ViralShorts",
    text: "Viral Shorts",
    icon: <ViralShotsIcon className="w-5 h-5" />,
  },
];

const MORE_KNOWLEDGE_BASES = [
  {
    id: "TVC",
    text: "TVC",
  },
  {
    id: "FilmParody",
    text: "Film Parody",
  },
  {
    id: "Explainer",
    text: "Explainer",
  },
];

export function KnowledgeBaseSelector({
  onSelect,
}: KnowledgeBaseSelectorProps) {
  const [knowledgeBases, setKnowledgeBases] = useState<
    SceneKnowledgeBaseDetail[]
  >([]);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchKnowledgeBases = async () => {
      setLoading(true);
      try {
        const data = await api.agent.listKnowledgeBases();
        if (isMounted) {
          setKnowledgeBases(data.data);
        }
      } catch (error) {
        console.error("Failed to fetch knowledge bases:", error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    void fetchKnowledgeBases();

    return () => {
      isMounted = false;
    };
  }, []);

  const publicKnowledgeBases = useMemo(() => {
    return PUBLIC_KNOWLEDGE_BASES.filter((k) =>
      knowledgeBases.some((kb) => kb.id === k.id)
    );
  }, [knowledgeBases]);

  const moreKnowledgeBases = useMemo(() => {
    return MORE_KNOWLEDGE_BASES.filter((k) =>
      knowledgeBases.some((kb) => kb.id === k.id)
    );
  }, [knowledgeBases]);

  if (loading && knowledgeBases.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-2 sm:gap-3 justify-center mt-7 px-3 sm:px-0">
      {publicKnowledgeBases.map((kb) => (
        <Button
          type="plain"
          key={kb.id}
          onClick={() => onSelect(kb)}
          icon={kb.icon}
          className="rounded-xl bg-[#111] border border-white/[.07] text-[#C0C4FA] gap-1 px-4 sm:px-5 h-10 hover:border-[rgba(82,81,246,0.21)] hover:opacity-70"
        >
          {kb.text}
        </Button>
      ))}

      {moreKnowledgeBases.length > 0 && (
        <Popover
          placement="topLeft"
          trigger="hover"
          arrow={false}
          classNames={{
            root: "",
            body: "p-0! bg-transparent!",
          }}
          content={
            <div className="border border-text-primary/[.04] rounded-xl bg-background-card/80 backdrop-blur-[15px] text-text-primary text-sm p-1 flex flex-col gap-1 items-start min-w-[140px]">
              {moreKnowledgeBases.map((kb) => (
                <Button
                  type="plain"
                  key={kb.id}
                  onClick={() => onSelect(kb)}
                  className="justify-start w-full h-8 pl-2 pr-5 hover:bg-background-card-hover rounded-lg"
                >
                  {kb.text}
                </Button>
              ))}
            </div>
          }
        >
          <Button
            type="plain"
            icon={<MoreIcon className="w-5 h-5" />}
            className="rounded-xl bg-[#111] border border-white/[.07] text-[#C0C4FA] gap-1 px-4 sm:px-5 h-10 hover:border-[rgba(82,81,246,0.21)]"
          >
            More
          </Button>
        </Popover>
      )}
    </div>
  );
}
